import React, { useState } from 'react';
import { Button } from 'reactstrap';
import { QUESTION_TYPE } from 'constants/constants';
import { parseHtml } from 'helpers';
import {
  DragAndDrop,
  FillInTheBlank,
  FillTheGap,
  FreeText,
  MultipleChoice,
  Ordering,
} from 'modules/Course/Unit/components/QuestionTypev2';
import styles from './mc-widgets.module.scss';

const shuffle = (items) => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

const emptyAnswer = (type, question) => {
  switch (type) {
    case QUESTION_TYPE.MTC:
      return (question?.data || []).map(() => false);
    case QUESTION_TYPE.FREETEXT:
      return '';
    case QUESTION_TYPE.ORDERING:
      return shuffle(
        (question?.data || []).map((item, itemIndex) => ({
          id: `${question._id}-${itemIndex}`,
          content: item.content || item,
        })),
      );
    case QUESTION_TYPE.DRAGDROP:
      return shuffle(
        (question?.data || []).map((item, itemIndex) => ({
          id: `${question._id}-d-${itemIndex}`,
          content: item.correct || item.content,
        })),
      );
    default:
      return [];
  }
};

const gradeAnswer = (question, value) => {
  const type = question?.type;
  if (type === QUESTION_TYPE.MTC) {
    const data = question.data || [];
    if (!data.length || !Array.isArray(value)) {
      return false;
    }
    return data.every(
      (item, index) => Boolean(item.correct) === Boolean(value[index]),
    );
  }
  if (type === QUESTION_TYPE.FILLINTHEBLANK) {
    const expected = (question.data || []).map((item) =>
      (Array.isArray(item) ? item : [item]).map((entry) =>
        String(entry).toLowerCase().trim(),
      ),
    );
    const given = (value || []).map((item) =>
      String(item || '').toLowerCase().trim(),
    );
    return (
      expected.length > 0 &&
      expected.every((opts, index) => opts.includes(given[index]))
    );
  }
  if (type === QUESTION_TYPE.FILLTHEGAP) {
    const expected = (question.data || []).map((item) => {
      if (Array.isArray(item)) {
        return item.map((entry) => String(entry).trim());
      }
      return [String(item).trim()];
    });
    const given = (value || []).map((item) => String(item || '').trim());
    return (
      expected.length > 0 &&
      expected.every((opts, index) => opts.includes(given[index]))
    );
  }
  if (type === QUESTION_TYPE.ORDERING) {
    const expected = (question.data || []).map((item) => item.content || item);
    const given = (value || []).map((item) => item.content || item);
    return (
      expected.length > 0 &&
      expected.every((item, index) => item === given[index])
    );
  }
  if (type === QUESTION_TYPE.DRAGDROP) {
    const expected = (question.data || []).map(
      (item) => item.correct || item.content,
    );
    const given = (value || []).map((item) => item.content || item);
    return (
      expected.length > 0 &&
      expected.every((item, index) => item === given[index])
    );
  }
  if (type === QUESTION_TYPE.FREETEXT) {
    return String(value || '').trim().length > 0;
  }
  return false;
};

const KnowledgeCheckItem = ({ question, index }) => {
  const type = question?.type;
  const [value, setValue] = useState(() => emptyAnswer(type, question));
  const [result, setResult] = useState(null);

  const handleCheck = () => {
    if (type === QUESTION_TYPE.FREETEXT) {
      setResult(String(value || '').trim() ? 'review' : 'empty');
      return;
    }
    setResult(gradeAnswer(question, value) ? 'correct' : 'wrong');
  };

  return (
    <div className={styles.item}>
      <div className={styles.stem}>
        <span className={styles.num}>{index + 1}.</span>
        {type !== QUESTION_TYPE.FILLINTHEBLANK && type !== QUESTION_TYPE.FILLTHEGAP
          ? parseHtml(question.content)
          : null}
        {type === QUESTION_TYPE.FILLINTHEBLANK && (
          <FillInTheBlank
            contentHTML={question.contentHTML || question.content}
            value={value}
            onChange={setValue}
            readOnly={result === 'correct'}
          />
        )}
        {type === QUESTION_TYPE.FILLTHEGAP && (
          <FillTheGap
            contentHTML={question.contentHTML || question.content}
            value={value}
            onChange={setValue}
            readOnly={result === 'correct'}
          />
        )}
      </div>
      {type === QUESTION_TYPE.MTC && (
        <MultipleChoice
          question={question.data ?? []}
          value={value}
          onChange={setValue}
          readOnly={result === 'correct'}
        />
      )}
      {type === QUESTION_TYPE.ORDERING && (
        <Ordering
          value={value}
          onChange={setValue}
          readOnly={result === 'correct'}
        />
      )}
      {type === QUESTION_TYPE.DRAGDROP && (
        <DragAndDrop
          questions={(question.data || []).map((item) => item.content)}
          value={value}
          onChange={setValue}
          readOnly={result === 'correct'}
        />
      )}
      {type === QUESTION_TYPE.FREETEXT && (
        <FreeText value={value} onChange={setValue} isFullscreenMode={false} />
      )}
      <div className={styles.actions}>
        <Button color="primary" size="sm" type="button" onClick={handleCheck}>
          Check answer
        </Button>
        {result === 'correct' && (
          <span className={`${styles.feedback} ${styles.correct}`}>Correct</span>
        )}
        {result === 'wrong' && (
          <span className={`${styles.feedback} ${styles.wrong}`}>Try again</span>
        )}
        {result === 'review' && (
          <span className={styles.feedback}>Answer recorded</span>
        )}
        {result === 'empty' && (
          <span className={`${styles.feedback} ${styles.wrong}`}>
            Enter an answer
          </span>
        )}
      </div>
    </div>
  );
};

const KnowledgeCheckHydrate = ({ questionIds = [], questions = [] }) => {
  const items = questionIds
    .map((id) => questions.find((question) => String(question?._id) === String(id)))
    .filter(Boolean);

  if (!items.length) {
    return (
      <div className={styles.empty}>
        Knowledge check questions are unavailable.
      </div>
    );
  }

  return (
    <div className={styles.live}>
      <h4 className={styles.title}>Knowledge check</h4>
      <p className={styles.subtitle}>Practice only — this does not complete the lesson.</p>
      {items.map((question, index) => (
        <KnowledgeCheckItem key={question._id} question={question} index={index} />
      ))}
    </div>
  );
};

export default KnowledgeCheckHydrate;
